import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { Blocks, Cable, CalendarDays, Clock, Landmark, Clapperboard, Dices, FileSignature, Video, FlaskConical, Mail, MessageCircle, Pencil, QrCode, RefreshCw, Search, Unplug, Wallet, Webhook } from 'lucide-react';
import { Botao, Cartao, Selo, type CorSelo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { aceitoDaUrl } from '../../componentes/filtros-lista';
import { ErroRequisicao, api, definirFiltroEmpresa, mensagemDeErro, post, textoDeErroGravado } from '../../nucleo/api';
import { censurarTexto, dataHora, email, relativo } from '../../nucleo/formato';
import { useSessao, veTodasAsEmpresas, type DadosSessao } from '../../nucleo/sessao';
import { CampoEmpresaDaTela, useEmpresaDaTela } from '../../nucleo/empresa/EmpresaDaTela';
import { BotaoCopiar, LinhaCopiavel } from './comum';
import type { CampoConector, Conector, EstadoConector, ResultadoTeste } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { TextoCortado } from '../../componentes/texto-cortado';
import { AvisoNaoOficial, PareamentoWhatsApp, numeroPareado } from './PareamentoWhatsApp';
import { ConexoesMaton } from './ConexoesMaton';

const ICONES: Record<string, ReactNode> = {
  'whatsapp-cloud-api': <MessageCircle className="size-5" aria-hidden />,
  'whatsapp-baileys': <MessageCircle className="size-5" aria-hidden />,
  'whatsapp-whatsmeow': <MessageCircle className="size-5" aria-hidden />,
  'instagram-messenger': <MessageCircle className="size-5" aria-hidden />,
  'google-calendar': <CalendarDays className="size-5" aria-hidden />,
  players: <Clapperboard className="size-5" aria-hidden />,
  asaas: <Wallet className="size-5" aria-hidden />,
  'sicredi-pix': <Wallet className="size-5" aria-hidden />,
  resend: <Mail className="size-5" aria-hidden />,
  'webhook-landing': <Webhook className="size-5" aria-hidden />,
  brasilapi: <Search className="size-5" aria-hidden />,
  'email-gmail': <Mail className="size-5" aria-hidden />,
  'email-imap': <Mail className="size-5" aria-hidden />,
  'conta-azul': <Landmark className="size-5" aria-hidden />,
  clicksign: <FileSignature className="size-5" aria-hidden />,
  documenso: <FileSignature className="size-5" aria-hidden />,
  jitsi: <Video className="size-5" aria-hidden />,
  controlid: <Clock className="size-5" aria-hidden />,
  zernio: <MessageCircle className="size-5" aria-hidden />,
  maton: <Blocks className="size-5" aria-hidden />,
};

const COR_ESTADO: Record<EstadoConector, CorSelo> = { conectado: 'verde', desconectado: 'neutro', expirado: 'laranja', erro: 'perigo' };

/**
 * Fase 2 (W33, item 0.7): selo e aviso do cartão. O Resend sem conector próprio conectado não é "desconectado" para quem lê:
 * com a chave da plataforma no servidor, os e-mails já saem pelo remetente padrão dela. Desconectado vira "Envio pela
 * plataforma" (selo ciano, o informativo da página) com o texto de que dá para ter remetente próprio; com erro ou expirado, o
 * selo fica e o aviso diz que, enquanto isso, o envio sai pela plataforma. O remetente da plataforma não aparece (a API só diz
 * se a chave existe). Função pura, testada em `tests/w33-paginas.test.ts`.
 */
export function situacaoDoCartao(c: Pick<Conector, 'tipo' | 'estado' | 'envio_plataforma'>): { cor: CorSelo; rotulo: string; aviso: string | null } {
  const pelaPlataforma = c.tipo === 'resend' && c.estado !== 'conectado' && c.envio_plataforma === true;
  if (pelaPlataforma && c.estado === 'desconectado') return { cor: 'ciano', rotulo: 'conectores.resend.plataforma', aviso: 'conectores.resend.plataformaTexto' };
  return { cor: COR_ESTADO[c.estado], rotulo: `estado.${c.estado}`, aviso: pelaPlataforma ? 'conectores.resend.plataformaReserva' : null };
}

/**
 * Review r19 visual (M4): campos não secretos que guardam e-mail (Resend: remetente e "Responder para"; Gmail: caixas
 * monitoradas; IMAP: usuário; Pix Sicredi: a chave Pix, que pode ser e-mail). E-mail é dado estruturado do modo discreto: no cartão sai pelo formatador e no formulário o
 * campo é `EntradaSensivel` (sem foco, com o modo ligado, vira o marcador).
 */
export const CAMPOS_COM_EMAIL: ReadonlySet<string> = new Set(['remetente', 'responder_para', 'caixas', 'usuario', 'chave_pix']);

/** Valor não secreto mostrado no cartão: e-mail só, por `email()`; "Nome <email>" ou lista de caixas, pela censura do texto. */
export function valorDoCampo(nome: string, valor: string | null | undefined) {
  if (!valor) return '';
  if (!CAMPOS_COM_EMAIL.has(nome)) return valor;
  return /^[^@\s<>,;]+@[^@\s<>,;]+$/.test(valor.trim()) ? email(valor.trim()) : censurarTexto(valor);
}

/**
 * Review r57 visual (M1): campo PEM não secreto (a cadeia do Sicredi) não aparece cru no cartão (milhares de caracteres no
 * resumo e no `title`): o resumo é quantos certificados ele tem. Null quando o valor não é PEM.
 */
export function certificadosNoPem(valor: string | null | undefined): number | null {
  if (!valor || !/-----BEGIN [A-Z0-9 ]+-----/.test(valor)) return null;
  return (valor.match(/-----BEGIN CERTIFICATE-----/g) ?? []).length;
}

/**
 * Review r57 (SPEC-B2 = VIS-B2): campo opcional e não secreto que estava gravado e a pessoa esvaziou vai em `limpar` (sai do
 * conector). Antes o vazio não ia ao servidor e a cadeia antiga voltava no cartão e no formulário.
 */
export function camposALimpar(campos: readonly Pick<CampoConector, 'nome' | 'secreto' | 'obrigatorio' | 'configurado'>[], valores: Record<string, string>): string[] {
  return campos.filter((c) => !c.secreto && !c.obrigatorio && c.configurado && (valores[c.nome] ?? '').trim() === '').map((c) => c.nome);
}

/**
 * Rodada rval1-1 (RVAL1-ROB-M2): com a troca de empresa desligada não há filtro de empresa no topo; a empresa dos conectores
 * é escolhida no campo "Empresa" da própria tela e vai no `X-Empresa` de cada requisição dela (a lista, salvar, testar...).
 * Com a troca ligada, `empresa` é nula e `cabecalho` vazio: vale o filtro do topo, como antes.
 */
const EmpresaDosConectores = createContext<{ empresa: string | null; cabecalho: Record<string, string> }>({ empresa: null, cabecalho: {} });
const useEmpresaDosConectores = () => useContext(EmpresaDosConectores);

/** Só os campos preenchidos vão para o servidor; vazio mantém o que já está guardado. E2: `escopo` todas ou só esta empresa. */
const salvarConector = (tipo: string, valores: Record<string, string>, escopo?: 'todas' | 'empresa', limpar: string[] = [], headers: Record<string, string> = {}) =>
  api('PUT', `/integracoes/conectores/${tipo}`, { campos: Object.fromEntries(Object.entries(valores).filter(([, v]) => v.trim() !== '')), ...(limpar.length ? { limpar } : {}), ...(escopo ? { escopo } : {}) }, { headers });

/**
 * E2 (conector por empresa): com mais de uma empresa, o conector de credencial escolhe se vale para todas as empresas ou só
 * para a atual (a do seletor). Sem empresa atual ("Todas as empresas"), só o de todas.
 */
export function useEscopoConector(c: Pick<Conector, 'por_empresa'>) {
  const s = useSessao();
  const daTela = useEmpresaDosConectores().empresa;
  const opcoes = s.empresas?.opcoes ?? [];
  const atual = opcoes.find((x) => x.id === (s.empresas?.atual ?? daTela)) ?? null;
  // Review r42 spec (M1): quem não vê todas as empresas nunca configura o de todas (D19): o "Vale para" aparece mesmo com
  // uma empresa só, fixo em "Só esta empresa".
  const soEmpresa = !veTodasAsEmpresas(s) && !!atual;
  return { mostra: !!c.por_empresa && (opcoes.length > 1 || soEmpresa), atual, soEmpresa: !!c.por_empresa && soEmpresa };
}

/**
 * Review r42 visual (M1): conector que só existe no de todas as empresas (Google, WhatsApp, e-mail, players...) não é
 * configurável por quem não vê todas (D19; o servidor sempre recusa): no lugar dos botões, a explicação.
 */
export const soGeralRestrito = (s: Pick<DadosSessao, 'empresas'>, c: Pick<Conector, 'por_empresa'>) => !c.por_empresa && !veTodasAsEmpresas(s);

/**
 * Review r43 spec (B2): quem não vê todas as empresas configura o conector só da empresa ATUAL (o servidor salva na do
 * seletor). Em "Todas as empresas" no seletor (consolidado) não há empresa atual: no lugar dos botões, o pedido de escolher
 * uma empresa no seletor do topo (antes, o "Vale para" só oferecia "Todas" e o salvar dava 403).
 */
export const semEmpresaRestrito = (s: Pick<DadosSessao, 'empresas'>, c: Pick<Conector, 'por_empresa'>, empresaDaTela: string | null = null) =>
  !!c.por_empresa && !veTodasAsEmpresas(s) && !(s.empresas?.atual ?? empresaDaTela);

/** Gera um valor aleatório legível para token de verificação ou segredo de webhook. */
function valorAleatorio(): string {
  const b = new Uint8Array(24);
  crypto.getRandomValues(b);
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

function FormularioConector({ conector, aoConcluir }: { conector: Conector; aoConcluir: () => void }) {
  const { t } = useTranslation('integracoes');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [valores, setValores] = useState<Record<string, string>>(() => Object.fromEntries(conector.campos.filter((c) => !c.secreto && c.valor).map((c) => [c.nome, c.valor as string])));
  const escopoConector = useEscopoConector(conector);
  const { cabecalho } = useEmpresaDosConectores();
  const [escopo, setEscopo] = useState<'todas' | 'empresa'>(conector.empresa || escopoConector.soEmpresa ? 'empresa' : 'todas');
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  useValidacaoLocal(erros, erroGeral);

  const salvar = useMutation({
    mutationFn: async (): Promise<ResultadoTeste | null> => {
      await salvarConector(conector.tipo, valores, escopoConector.mostra ? escopo : undefined, camposALimpar(conector.campos, valores), cabecalho);
      // Fase 2 (W30): no WhatsApp não oficial, o "teste" é o pareamento; salvar o ritmo só salva.
      if (conector.pareamento) return null;
      // Trilha Conta Azul: salvar o app não testa (falta autorizar); o teste vem depois do "Autorizar".
      if (conector.autorizacao) return null;
      return post<ResultadoTeste>(`/integracoes/conectores/${conector.tipo}/testar`, undefined, cabecalho);
    },
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['integracoes', 'conectores'] });
      if (!r) toast.sucesso(tn('comum.salvo'));
      else if (r.estado === 'conectado') toast.sucesso(t('conectores.resultado.conectado'));
      else toast.erro(`${t(`conectores.resultado.${r.estado}`)}${r.erro ? `: ${textoDeErroGravado(r.erro)}` : ''}`);
      aoConcluir();
    },
    onError: (e) => {
      if (e instanceof ErroRequisicao) {
        const porCampo = e.porCampo();
        setErros(porCampo);
        setErroGeral(Object.keys(porCampo).length ? null : mensagemDeErro(e));
      } else setErroGeral(mensagemDeErro(e));
    },
  });

  function campoDe(c: CampoConector) {
    const rotulo = t(`conectores.campos.${c.nome}`);
    const id = `cc-${conector.tipo}-${c.nome}`;
    // Trilha Conta Azul: ajuda própria do tipo quando o mesmo nome de campo significa outra coisa (o `ambiente` do Asaas).
    const ajuda = t(`conectores.ajudasPorTipo.${conector.tipo}.${c.nome}`, { defaultValue: t(`conectores.ajudas.${c.nome}`, { defaultValue: '' }) });
    const jaTem = c.configurado;
    const dica = c.secreto && jaTem ? (c.ultimos4 ? t('conectores.configuradaFim', { fim: c.ultimos4 }) : t('conectores.configurada')) : undefined;
    // 22/10 (Gustavo): o segredo que o cartão gera (webhook do Documenso, da Clicksign, da Maton...) fica à vista e com
    // "Copiar" até salvar, para colar no provedor; depois de salvo ele não volta (só os últimos 4), como todo segredo.
    const gerado = !!c.gerar && !!valores[c.nome];
    const copieAgora = gerado ? t('conectores.copieAgora') : undefined;
    return (
      <Rotulo key={c.nome} id={id} rotulo={rotulo} obrigatorio={c.obrigatorio && !jaTem} erro={erros[c.nome]} ajuda={[copieAgora, dica, c.secreto && jaTem ? t('conectores.deixeVazio') : undefined, ajuda || undefined].filter(Boolean).join('. ')}>
        {c.tipo === 'selecao' ? (
          <Selecao id={id} opcoes={c.opcoes.map((o) => ({ valor: o, rotulo: t(`conectores.ambientes.${o}`, { defaultValue: o }) }))} vazio="" value={valores[c.nome] ?? ''} onChange={(e) => setValores((v) => ({ ...v, [c.nome]: e.target.value }))} />
        ) : CAMPOS_COM_EMAIL.has(c.nome) ? (
          <EntradaSensivel mascara="email" id={id} rotulo={rotulo} autoComplete="off" spellCheck={false} value={valores[c.nome] ?? ''} onChange={(e) => setValores((v) => ({ ...v, [c.nome]: e.target.value }))} />
        ) : (
          <div className="flex gap-2">
            <Entrada
              id={id}
              type={c.secreto && !gerado ? 'password' : 'text'}
              autoComplete="off"
              spellCheck={false}
              placeholder={c.secreto && jaTem ? '••••••••' : undefined}
              value={valores[c.nome] ?? ''}
              onChange={(e) => setValores((v) => ({ ...v, [c.nome]: e.target.value }))}
            />
            {c.gerar && (
              <Botao aria-label={t('conectores.acoes.gerar')} title={t('conectores.acoes.gerar')} icone={<Dices className="size-4" aria-hidden />} onClick={() => setValores((v) => ({ ...v, [c.nome]: valorAleatorio() }))}>
                <span className="hidden sm:inline">{t('conectores.acoes.gerar')}</span>
              </Botao>
            )}
            {gerado && <BotaoCopiar texto={valores[c.nome]!} />}
          </div>
        )}
      </Rotulo>
    );
  }

  return (
    <form
      id={`form-conector-${conector.tipo}`}
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        setErros({});
        setErroGeral(null);
        salvar.mutate();
      }}
    >
      <p className="text-sm text-tinta-3">{t(`conectores.tipos.${conector.tipo}.descricao`)}</p>
      {erroGeral && (
        <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
          {erroGeral}
        </p>
      )}
      {escopoConector.mostra && (
        <Rotulo id={`cc-${conector.tipo}-escopo`} rotulo={t('conectores.empresa.valePara')} ajuda={escopo === 'empresa' && !conector.empresa ? t('conectores.empresa.ajudaNova') : undefined}>
          <Selecao
            id={`cc-${conector.tipo}-escopo`}
            value={escopo}
            onChange={(e) => setEscopo(e.target.value as 'todas' | 'empresa')}
            opcoes={[
              ...(escopoConector.soEmpresa ? [] : [{ valor: 'todas', rotulo: t('conectores.empresa.todas') }]),
              ...(escopoConector.atual ? [{ valor: 'empresa', rotulo: t('conectores.empresa.soEsta', { nome: escopoConector.atual.nome }) }] : []),
            ]}
          />
        </Rotulo>
      )}
      {conector.campos.map(campoDe)}
      {conector.webhook_url && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-tinta-2">{t('conectores.webhookUrl')}</span>
          <LinhaCopiavel texto={conector.webhook_url} />
          <span className="text-xs text-tinta-3">{t('conectores.webhookAjuda')}</span>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Botao variante="fantasma" onClick={aoConcluir}>
          {tn('comum.cancelar')}
        </Botao>
        <Botao type="submit" variante="primario" carregando={salvar.isPending}>
          {conector.pareamento ? tn('comum.salvar') : t('conectores.acoes.salvarTestar')}
        </Botao>
      </div>
    </form>
  );
}

/**
 * Review r43 spec (B2) = visual (M1): o restrito em "Todas as empresas" no seletor escolhe aqui uma das empresas dele
 * (sem "Todas"); a escolha troca a empresa da sessão como o seletor do topo, e o cartão volta com os botões dela.
 * Review r44 visual (M1): a lista só escolhe; quem troca a sessão é o botão "Usar esta empresa" (a seta do teclado numa
 * lista fechada dispara `change` no Chromium: nenhuma lista do sistema age no `onChange`).
 */
function EscolherEmpresaConector({ tipo }: { tipo: string }) {
  const { t } = useTranslation('integracoes');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const id = `cc-${tipo}-empresa`;
  const [escolha, setEscolha] = useState('');
  const trocar = useMutation({
    // V2 verticais: filtra a aba na empresa (o filtro do topo), em vez de gravar a escolha na sessão.
    mutationFn: async (empresa: string) => definirFiltroEmpresa(empresa),
    onSuccess: () => qc.invalidateQueries(),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <div data-conector-escolha-empresa="" className="mt-4 flex flex-col gap-2 border-t border-borda pt-4">
      <Rotulo id={id} rotulo={t('conectores.empresa.valePara')} obrigatorio ajuda={t('conectores.empresa.escolhaEmpresa')}>
        <Selecao
          id={id}
          vazio=""
          value={escolha}
          required
          aria-describedby={`${id}-ajuda`}
          disabled={trocar.isPending}
          opcoes={(s.empresas?.opcoes ?? []).map((e) => ({ valor: e.id, rotulo: t('conectores.empresa.soEsta', { nome: e.nome }) }))}
          onChange={(e) => setEscolha(e.target.value)}
        />
      </Rotulo>
      <div className="flex justify-end">
        <Botao variante="secundario" disabled={!escolha} carregando={trocar.isPending} onClick={() => escolha && trocar.mutate(escolha)}>
          {t('conectores.empresa.usarEsta')}
        </Botao>
      </div>
    </div>
  );
}

function CartaoConector({ c }: { c: Conector }) {
  const { t } = useTranslation('integracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [editando, setEditando] = useState(false);
  const [pareando, setPareando] = useState(false);
  const [desligando, setDesligando] = useState(false);
  const ehEmail = c.tipo === 'email-gmail' || c.tipo === 'email-imap';
  const pode = s.pode('integracoes.conectores');
  const par = c.pareamento ?? null;
  const { empresa: empresaDaTela, cabecalho } = useEmpresaDosConectores();
  const invalidar = () => qc.invalidateQueries({ queryKey: ['integracoes', 'conectores'] });

  const testar = useMutation({
    mutationFn: () => post<ResultadoTeste>(`/integracoes/conectores/${c.tipo}/testar`, undefined, cabecalho),
    onSuccess: async (r) => {
      await invalidar();
      if (r.estado === 'conectado') toast.sucesso(t('conectores.resultado.conectado'));
      else toast.erro(`${t(`conectores.resultado.${r.estado}`)}${r.erro ? `: ${textoDeErroGravado(r.erro)}` : ''}`);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const habilitar = useMutation({
    mutationFn: async () => {
      await salvarConector(c.tipo, {}, undefined, [], cabecalho);
      return post<ResultadoTeste>(`/integracoes/conectores/${c.tipo}/testar`, undefined, cabecalho);
    },
    onSuccess: async () => {
      await invalidar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Fase 2 (W30): desconectar o WhatsApp não oficial (sai no gateway e apaga o estado da sessão).
  const desconectar = useMutation({
    mutationFn: () => post<{ gateway_avisado: boolean }>(`/integracoes/conectores/${c.tipo}/desconectar`, undefined, cabecalho),
    onSuccess: async (r) => {
      setDesligando(false);
      await invalidar();
      if (ehEmail) toast.sucesso(t('conectores.email.desconectado'));
      else if (r.gateway_avisado) toast.sucesso(t('conectores.whatsapp.desconectado'));
      else toast.erro(t('conectores.whatsapp.desconectadoSemGateway'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // E2: a empresa atual deixa o conector próprio e volta ao de todas as empresas.
  const usarGeral = useMutation({
    mutationFn: () => post(`/integracoes/conectores/${c.tipo}/usar-geral`, undefined, cabecalho),
    onSuccess: async () => {
      await invalidar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const conectarGoogle = useMutation({
    mutationFn: () => post<{ url: string }>('/integracoes/conectores/google-calendar/conectar', undefined, cabecalho),
    onSuccess: (r) => {
      window.location.assign(r.url);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  // U14 F3: a Zernio prepara o perfil e o webhook e devolve o link hospedado dela, onde a pessoa conecta o número.
  const conectarZernio = useMutation({
    mutationFn: () => post<{ url: string }>('/integracoes/conectores/zernio/conectar', undefined, cabecalho),
    onSuccess: (r) => {
      window.location.assign(r.url);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  // Trilha Conta Azul: o consentimento é na Conta Azul (no dublê, o retorno é direto).
  const autorizarContaAzul = useMutation({
    mutationFn: () => post<{ url: string }>('/integracoes/conectores/conta-azul/conectar', undefined, cabecalho),
    onSuccess: (r) => {
      window.location.assign(r.url);
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const precisaReconectar = c.estado === 'expirado' || c.estado === 'erro';
  const podeTestar = c.autorizacao ? c.autorizacao.autorizado : c.estado !== 'desconectado' || c.campos.some((x) => x.configurado);
  const appSalvo = !!c.autorizacao && !c.campos.some((x) => x.obrigatorio && !x.configurado);
  const ocupado = testar.isPending || habilitar.isPending || conectarGoogle.isPending || desconectar.isPending || autorizarContaAzul.isPending || conectarZernio.isPending;
  const temSessao = !!par && (c.estado !== 'desconectado' || !!par.sessao_estado);
  // Fase 2 (W30): a API oficial também desliga (para migrar a Baileys ou whatsmeow; a credencial fica guardada).
  // U14 F3: a Zernio também só desliga (o número continua conectado nela).
  const oficialLigada = (c.tipo === 'whatsapp-cloud-api' || c.tipo === 'zernio') && c.estado === 'conectado';
  // D14 (14/10): o e-mail do atendimento também desliga (a credencial fica e o Testar conexão liga de novo).
  const emailLigado = ehEmail && c.estado !== 'desconectado';
  const situacao = situacaoDoCartao(c);

  return (
    <Cartao className="flex flex-col">
      <div className="flex items-start gap-3">
        <span className="rounded-lg border border-borda bg-sup-2 p-2 text-tinta-2">{ICONES[c.tipo] ?? <Cable className="size-5" aria-hidden />}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold">{t(`conectores.tipos.${c.tipo}.nome`)}</h2>
              {/* Fase 2 (W30): a API oficial é o padrão recomendado; os outros dois são não oficiais. */}
              {c.tipo === 'whatsapp-cloud-api' && <Selo cor="verde">{t('conectores.whatsapp.recomendado')}</Selo>}
              {par && <Selo cor="laranja">{t('conectores.whatsapp.naoOficial')}</Selo>}
              {/* E2: o que vale para a empresa atual é o conector só dela. */}
              {c.empresa && <Selo cor="ciano">{t('conectores.empresa.selo')}</Selo>}
            </div>
            <span data-estado-conector={situacao.aviso ? 'plataforma' : c.estado}>
              <Selo cor={situacao.cor}>{t(situacao.rotulo)}</Selo>
            </span>
          </div>
          <p className="mt-1 text-sm text-tinta-3">{t(`conectores.tipos.${c.tipo}.descricao`)}</p>
        </div>
      </div>

      <div className="mt-4 flex min-w-0 flex-col gap-3 text-sm">
        {situacao.aviso && (
          <p data-aviso-plataforma="" className="text-xs text-tinta-2">
            {t(situacao.aviso)}
          </p>
        )}
        {c.tipo === 'players' && c.habilitados.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-tinta-3">{t('conectores.players.habilitados')}</span>
            {c.habilitados.map((p) => (
              <Selo key={p} cor="ciano">
                {t(`conectores.players.nomes.${p}`, { defaultValue: p })}
              </Selo>
            ))}
          </div>
        )}
        {/* Fase 2 (W26): a escolha da agenda de cada profissional mora na Agenda > Profissionais e recursos. */}
        {c.tipo === 'google-calendar' && c.estado === 'conectado' && typeof c.sincronizados === 'number' && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-tinta-2" data-testid="google-sincronizados">
              {t('conectores.sincronizados', { count: c.sincronizados })}
            </span>
            {s.instalado('agenda') && s.pode('agenda.ver') && (
              <Link to="/cadastros/agenda/recursos" className="w-fit text-sm font-medium text-acento-forte hover:underline">
                {t('conectores.escolherAgendas')}
              </Link>
            )}
          </div>
        )}
        {par && <AvisoNaoOficial />}
        {par && !par.configurado && <p className="rounded-lg border border-laranja px-3 py-2 text-xs text-tinta-2">{t('conectores.whatsapp.naoConfigurado')}</p>}
        {par && (par.numero || par.sessao_estado) && (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            {par.numero && (
              <div className="flex min-w-0 justify-between gap-2">
                <dt className="shrink-0 text-tinta-3">{t('conectores.whatsapp.numero')}</dt>
                <TextoCortado como="dd" className="numero min-w-0 text-tinta-2">{numeroPareado(par.numero)}</TextoCortado>
              </div>
            )}
            {par.sessao_estado && (
              <div className="flex min-w-0 justify-between gap-2">
                <dt className="shrink-0 text-tinta-3">{t('conectores.whatsapp.sessao')}</dt>
                <dd className="min-w-0 text-tinta-2">
                  {t(`conectores.whatsapp.estados.${par.sessao_estado}`, { defaultValue: par.sessao_estado })}
                  {/* W40: fim do bloqueio temporário ou da restrição da conta. */}
                  {par.ate_ms ? ` ${t('conectores.whatsapp.ate', { quando: dataHora(new Date(par.ate_ms)) })}` : ''}
                </dd>
              </div>
            )}
          </dl>
        )}
        {c.zernio?.numero && (
          <dl className="grid grid-cols-1 gap-y-1 text-xs">
            <div className="flex min-w-0 justify-between gap-2">
              <dt className="shrink-0 text-tinta-3">{t('conectores.zernio.numero')}</dt>
              <TextoCortado como="dd" className="numero min-w-0 text-tinta-2">{numeroPareado(c.zernio.numero)}</TextoCortado>
            </div>
          </dl>
        )}
        {c.zernio && !c.zernio.pronto && pode && <p className="text-xs text-tinta-3">{t('conectores.zernio.salvarAntes')}</p>}
        {c.oauth && !c.oauth.disponivel && (
          <p className="rounded-lg border border-laranja px-3 py-2 text-xs text-tinta-2">{t('conectores.google.naoConfigurado')}</p>
        )}
        {c.oauth && pode && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-tinta-3">{t('conectores.google.redirectUri')}</span>
            <LinhaCopiavel texto={c.oauth.redirect_uri} />
          </div>
        )}
        {c.autorizacao && pode && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-tinta-3">{t('conectores.contaAzul.redirectUri')}</span>
            <LinhaCopiavel texto={c.autorizacao.redirect_uri} />
          </div>
        )}
        {c.webhook_url && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-tinta-3">{t('conectores.webhookUrl')}</span>
            <LinhaCopiavel texto={c.webhook_url} />
          </div>
        )}
        {/* U14 F6: as conexões da Maton (contas dos apps) com a chave salva; os gatilhos criados pelas automações. */}
        {c.maton && !c.maton.pronto && pode && <p className="text-xs text-tinta-3">{t('conectores.maton.salvarAntes')}</p>}
        {c.maton?.pronto && c.estado !== 'desconectado' && <ConexoesMaton pode={pode} />}
        {c.maton?.pronto && c.maton.gatilhos > 0 && <p className="text-xs text-tinta-3">{t('conectores.maton.gatilhos', { count: c.maton.gatilhos })}</p>}
        {c.campos.some((x) => x.configurado) && !c.campos.some((x) => x.obrigatorio && !x.configurado) && (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            {c.campos
              .filter((x) => x.configurado)
              .map((x) => (
                // Review r57 visual (M1): o rótulo fica inteiro enquanto cabe (o valor é que corta) e, maior que a linha, corta
                // com `title` em vez de sair do cartão; o PEM vira o resumo curto, que não corta (o rótulo cede).
                <div key={x.nome} className="flex min-w-0 justify-between gap-2">
                  <TextoCortado como="dt" className={`min-w-0 text-tinta-3 ${!x.secreto && certificadosNoPem(x.valor) !== null ? '' : 'max-w-full shrink-0'}`}>
                    {t(`conectores.campos.${x.nome}`)}
                  </TextoCortado>
                  {!x.secreto && certificadosNoPem(x.valor) !== null ? (
                    <dd className="shrink-0 text-tinta-2">{certificadosNoPem(x.valor) ? t('conectores.certificados', { count: certificadosNoPem(x.valor)! }) : t('conectores.configurada')}</dd>
                  ) : (
                    <TextoCortado como="dd" className="numero min-w-0 text-tinta-2">{x.secreto ? (x.ultimos4 ? `••••${x.ultimos4}` : t('conectores.configurada')) : valorDoCampo(x.nome, x.valor)}</TextoCortado>
                  )}
                </div>
              ))}
          </dl>
        )}
        {c.ultimo_erro && c.estado !== 'conectado' && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-xs break-words text-perigo">
            {textoDeErroGravado(c.ultimo_erro)}
          </p>
        )}
        <p className="text-xs text-tinta-3">{c.ultimo_teste ? t('conectores.ultimoTeste', { quando: relativo(c.ultimo_teste) }) : t('conectores.nuncaTestado')}</p>
      </div>

      {pode && soGeralRestrito(s, c) && (
        <p data-conector-so-geral="" className="mt-4 border-t border-borda pt-4 text-xs text-tinta-3">
          {t('conectores.empresa.soGeral')}
        </p>
      )}
      {pode && semEmpresaRestrito(s, c, empresaDaTela) && <EscolherEmpresaConector tipo={c.tipo} />}
      {pode && !soGeralRestrito(s, c) && !semEmpresaRestrito(s, c, empresaDaTela) && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-borda pt-4">
          {c.oauth ? (
            <Botao
              variante="secundario"
              disabled={!c.oauth.disponivel}
              carregando={conectarGoogle.isPending}
              icone={precisaReconectar || c.estado === 'conectado' ? <RefreshCw className="size-4" aria-hidden /> : undefined}
              onClick={() => conectarGoogle.mutate()}
            >
              {c.estado === 'desconectado' ? t('conectores.acoes.conectar') : t('conectores.acoes.reconectar')}
            </Botao>
          ) : par ? (
            <>
              {c.estado !== 'conectado' && (
                <Botao variante="secundario" icone={<QrCode className="size-4" aria-hidden />} disabled={!par.configurado} onClick={() => setPareando(true)}>
                  {t('conectores.whatsapp.parear')}
                </Botao>
              )}
              <Botao variante="secundario" icone={<Pencil className="size-4" aria-hidden />} onClick={() => setEditando(true)}>
                {t('conectores.whatsapp.ritmo')}
              </Botao>
              {temSessao && (
                <Botao variante="perigo" icone={<Unplug className="size-4" aria-hidden />} disabled={ocupado && !desconectar.isPending} onClick={() => setDesligando(true)}>
                  {t('conectores.whatsapp.desconectar')}
                </Botao>
              )}
            </>
          ) : c.sem_credencial ? (
            c.estado !== 'conectado' && (
              <Botao variante="secundario" carregando={habilitar.isPending} onClick={() => habilitar.mutate()}>
                {c.tipo === 'players' ? t('conectores.acoes.habilitar') : t('conectores.acoes.habilitarConector')}
              </Botao>
            )
          ) : (
            <Botao variante="secundario" icone={precisaReconectar ? <RefreshCw className="size-4" aria-hidden /> : <Pencil className="size-4" aria-hidden />} onClick={() => setEditando(true)}>
              {precisaReconectar ? t('conectores.acoes.reconectar') : t('conectores.acoes.configurar')}
            </Botao>
          )}
          {appSalvo && (
            <Botao
              icone={c.autorizacao?.autorizado ? <RefreshCw className="size-4" aria-hidden /> : undefined}
              carregando={autorizarContaAzul.isPending}
              disabled={ocupado && !autorizarContaAzul.isPending}
              onClick={() => autorizarContaAzul.mutate()}
            >
              {c.autorizacao?.autorizado ? t('conectores.contaAzul.autorizarDeNovo') : t('conectores.contaAzul.autorizar')}
            </Botao>
          )}
          {c.zernio?.pronto && (
            <Botao
              icone={c.zernio.numero ? <RefreshCw className="size-4" aria-hidden /> : <QrCode className="size-4" aria-hidden />}
              carregando={conectarZernio.isPending}
              disabled={ocupado && !conectarZernio.isPending}
              onClick={() => conectarZernio.mutate()}
            >
              {c.zernio.numero ? t('conectores.zernio.outroNumero') : t('conectores.zernio.conectar')}
            </Botao>
          )}
          {podeTestar && (
            <Botao icone={<FlaskConical className="size-4" aria-hidden />} carregando={testar.isPending} disabled={ocupado && !testar.isPending} onClick={() => testar.mutate()}>
              {t('conectores.acoes.testar')}
            </Botao>
          )}
          {(oficialLigada || emailLigado) && (
            <Botao variante="perigo" icone={<Unplug className="size-4" aria-hidden />} disabled={ocupado && !desconectar.isPending} onClick={() => setDesligando(true)}>
              {t('conectores.whatsapp.desconectar')}
            </Botao>
          )}
          {c.empresa && (
            <Botao variante="fantasma" carregando={usarGeral.isPending} disabled={ocupado} onClick={() => usarGeral.mutate()}>
              {t('conectores.empresa.usarGeral')}
            </Botao>
          )}
        </div>
      )}

      <Drawer aberto={editando} aoFechar={() => setEditando(false)} titulo={t(`conectores.tipos.${c.tipo}.nome`)}>
        {editando && <FormularioConector conector={c} aoConcluir={() => setEditando(false)} />}
      </Drawer>
      {ehEmail && (
        <Confirmar
          aberto={desligando}
          aoFechar={() => setDesligando(false)}
          aoConfirmar={() => desconectar.mutate()}
          carregando={desconectar.isPending}
          perigoso
          titulo={t('conectores.email.confirmarTitulo')}
          texto={t('conectores.email.confirmarTexto')}
        />
      )}
      {(c.tipo === 'whatsapp-cloud-api' || c.tipo === 'zernio') && (
        <Confirmar
          aberto={desligando}
          aoFechar={() => setDesligando(false)}
          aoConfirmar={() => desconectar.mutate()}
          carregando={desconectar.isPending}
          perigoso
          titulo={t('conectores.whatsapp.confirmarTitulo')}
          texto={c.tipo === 'zernio' ? t('conectores.zernio.confirmar') : t('conectores.whatsapp.confirmarOficial')}
        />
      )}
      {par && (
        <>
          <Drawer aberto={pareando} aoFechar={() => setPareando(false)} titulo={t('conectores.whatsapp.tituloPareamento', { nome: t(`conectores.tipos.${c.tipo}.nome`) })}>
            {pareando && <PareamentoWhatsApp conector={c} aoConcluir={() => setPareando(false)} />}
          </Drawer>
          <Confirmar
            aberto={desligando}
            aoFechar={() => setDesligando(false)}
            aoConfirmar={() => desconectar.mutate()}
            carregando={desconectar.isPending}
            perigoso
            titulo={t('conectores.whatsapp.confirmarTitulo')}
            texto={t('conectores.whatsapp.confirmarTexto')}
          />
        </>
      )}
    </Cartao>
  );
}

/**
 * Review r54 visual (B3): os motivos que a volta do consentimento pode trazer na URL (`motivo=`), os mesmos do servidor
 * (`voltar(...)` em conectores.ts e conta-azul/autorizar.ts). Valor fora da lista vira o motivo genérico: texto livre da URL
 * (um link montado por terceiros) nunca entra no aviso do sistema.
 */
const MOTIVOS_GOOGLE = ['state', 'negado', 'troca', 'sem_codigo', 'google'] as const;
const MOTIVOS_CONTA_AZUL = ['state', 'negado', 'troca', 'sem_codigo', 'conflito', 'contaazul'] as const;
/** U14 F3: os erros que o link hospedado da Zernio devolve na volta (`error=`); o resto vira o genérico. */
const MOTIVOS_ZERNIO = ['whatsapp_error', 'one_whatsapp_per_profile', 'whatsapp_number_already_connected', 'whatsapp_number_pinned_to_profile', 'connection_cancelled', 'payment_required', 'session_expired'] as const;
export const chaveDoMotivoZernio = (motivo: string | null) => `conectores.zernio.motivos.${aceitoDaUrl(motivo, MOTIVOS_ZERNIO) || 'zernio'}`;

/** A chave do motivo da volta do consentimento: só código conhecido; o resto, o genérico do provedor. */
export function chaveDoMotivoOAuth(provedor: 'google' | 'contaAzul', motivo: string | null): string {
  const conhecido = aceitoDaUrl(motivo, provedor === 'google' ? MOTIVOS_GOOGLE : MOTIVOS_CONTA_AZUL);
  return `conectores.${provedor}.motivos.${conhecido || (provedor === 'google' ? 'google' : 'contaazul')}`;
}

export function Conectores() {
  const { t } = useTranslation('integracoes');
  const toast = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const s = useSessao();
  // Rodada rval1-1 (RVAL1-ROB-M2): troca de empresa desligada, mais de uma empresa: quem vê todas começa em "Minhas verticais"
  // (o conector de todas as empresas); quem é limitado, na primeira empresa dele (ele só configura o da empresa).
  const daTela = useEmpresaDaTela({ comTodas: veTodasAsEmpresas(s) });
  const q = useQuery({
    queryKey: daTela.mostra ? ['integracoes', 'conectores', daTela.empresa ?? ''] : ['integracoes', 'conectores'],
    queryFn: () => api<{ itens: Conector[] }>('GET', '/integracoes/conectores', undefined, { headers: daTela.cabecalho }),
  });
  // U14 F3: a volta da Zernio é tratada uma vez só (o efeito roda duas vezes no modo estrito do React; o Testar não repete).
  const voltaZernioTratada = useRef(false);

  // Volta do consentimento do Google (ou da Conta Azul): avisa o resultado e limpa a URL.
  useEffect(() => {
    // U14 F3: volta do link hospedado da Zernio. Com erro, o motivo; sem erro, o Testar acha a conta do número e liga o conector.
    if (params.get('zernio') === 'retorno') {
      if (voltaZernioTratada.current) return;
      voltaZernioTratada.current = true;
      const erro = params.get('error');
      setParams({}, { replace: true });
      if (erro) {
        toast.erro(t('conectores.zernio.erro', { motivo: t(chaveDoMotivoZernio(erro)) }));
        return;
      }
      void post<ResultadoTeste>('/integracoes/conectores/zernio/testar', undefined, daTela.cabecalho)
        .then((r) => {
          if (r.estado === 'conectado') toast.sucesso(t('conectores.zernio.conectado'));
          else toast.erro(`${t(`conectores.resultado.${r.estado}`)}${r.erro ? `: ${textoDeErroGravado(r.erro)}` : ''}`);
        })
        .catch((e: unknown) => toast.erro(mensagemDeErro(e)))
        .finally(() => void qc.invalidateQueries({ queryKey: ['integracoes', 'conectores'] }));
      return;
    }
    const ca = params.get('contaazul');
    if (ca) {
      if (ca === 'conectado') toast.sucesso(t('conectores.contaAzul.conectado'));
      else toast.erro(t('conectores.contaAzul.erro', { motivo: t(chaveDoMotivoOAuth('contaAzul', params.get('motivo'))) }));
      void qc.invalidateQueries({ queryKey: ['integracoes'] });
      setParams({}, { replace: true });
      return;
    }
    const g = params.get('google');
    if (!g) return;
    if (g === 'conectado') toast.sucesso(t('conectores.google.conectado'));
    else toast.erro(t('conectores.google.erro', { motivo: t(chaveDoMotivoOAuth('google', params.get('motivo'))) }));
    void qc.invalidateQueries({ queryKey: ['integracoes', 'conectores'] });
    setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <EmpresaDosConectores.Provider value={{ empresa: daTela.mostra ? daTela.empresa : null, cabecalho: daTela.cabecalho }}>
      <div className="flex flex-col gap-4">
        <p className="max-w-3xl text-sm text-tinta-3">{t('conectores.intro')}</p>
        <CampoEmpresaDaTela id="conectores-empresa" estado={daTela} />
        <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
          {(d) => (
            <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
              {d.itens.map((c) => (
                <CartaoConector key={c.tipo} c={c} />
              ))}
            </div>
          )}
        </Estado>
      </div>
    </EmpresaDosConectores.Provider>
  );
}
